"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { Select } from "@base-ui/react/select";
import { useCallback, useId, useMemo, useRef, useState } from "react";
import { usePortalContainer } from "../../contexts/index.js";
import { cn } from "../../utils/cn.js";
import { CdnIcon } from "../CdnIcon/index.js";
import { Tooltip } from "../Tooltip/index.js";
import { selectVariants } from "./Select.variants.js";
function Select_Select({ label, isLabelHidden, helperText, errorText, errorTooltipText, isError, iconLeft, className, menuItems, placeholder, isInset, size, value: controlledValue, initialValue, isDisabled, menuWidth, onChange, onOpenChange, slots = {
    inputWrapper: [],
    popupPositioner: ''
}, ...restProps }) {
    const isControlled = void 0 !== controlledValue;
    const portalContainer = usePortalContainer();
    const labelId = useId();
    const hasLabel = Boolean(label);
    const validatedInitialValue = useMemo(()=>{
        if (!initialValue || !menuItems) return '';
        return menuItems.some((item)=>item.value === initialValue) ? initialValue : '';
    }, [
        initialValue,
        menuItems
    ]);
    const [internalValue, setInternalValue] = useState(validatedInitialValue);
    const value = isControlled ? controlledValue : internalValue;
    const [isErrorNew, setIsErrorNew] = useState(isError);
    const [menuOpen, setMenuOpen] = useState(false);
    const triggerRef = useRef(null);
    let displayedValue = menuItems?.find((val)=>val.value === value)?.label ?? placeholder;
    if (isInset && !displayedValue) displayedValue = label;
    const handleOnValueChange = useCallback((newValue, eventDetails)=>{
        eventDetails.event?.stopPropagation();
        if (isErrorNew) setIsErrorNew(false);
        const stringValue = 'string' == typeof newValue ? newValue : '';
        if (!isControlled) setInternalValue(stringValue);
        onChange?.(stringValue);
    }, [
        isErrorNew,
        isControlled,
        onChange
    ]);
    const handleOnOpenChange = useCallback((open, eventDetails)=>{
        eventDetails.event?.stopPropagation();
        setMenuOpen(open);
        onOpenChange?.(open);
    }, [
        onOpenChange
    ]);
    const styles = selectVariants({
        hasIconLeft: !!iconLeft,
        isInset,
        hasValue: Boolean(value),
        hasPlaceholder: Boolean(placeholder),
        size,
        isError: isErrorNew,
        isDisabled,
        isLabelHidden,
        menuOpen
    });
    const { inputWrapper, popupPositioner } = slots;
    const triggerClassName = cn(styles.inputWrapper(), inputWrapper);
    const popupPositionerClassName = cn(styles.popupPositioner(), popupPositioner);
    return /*#__PURE__*/ jsx(Select.Root, {
        value: value,
        onValueChange: handleOnValueChange,
        disabled: isDisabled,
        onOpenChange: handleOnOpenChange,
        ...restProps,
        modal: false,
        "data-testid": "select-component",
        children: /*#__PURE__*/ jsxs("div", {
            className: cn(styles.root(), className),
            children: [
                label && /*#__PURE__*/ jsx("div", {
                    id: labelId,
                    className: styles.label(),
                    children: label
                }),
                /*#__PURE__*/ jsxs(Select.Trigger, {
                    ref: triggerRef,
                    className: triggerClassName,
                    "aria-labelledby": hasLabel ? labelId : void 0,
                    "aria-label": hasLabel ? void 0 : placeholder,
                    children: [
                        iconLeft ? /*#__PURE__*/ jsx("div", {
                            className: styles.iconLeft(),
                            children: /*#__PURE__*/ jsx(CdnIcon, {
                                iconName: iconLeft,
                                size: "24"
                            })
                        }) : null,
                        /*#__PURE__*/ jsx(Select.Value, {
                            className: styles.input(),
                            children: displayedValue
                        }),
                        isErrorNew && errorTooltipText && /*#__PURE__*/ jsx(Tooltip, {
                            side: "bottom",
                            align: "center",
                            triggerNode: /*#__PURE__*/ jsx(CdnIcon, {
                                iconName: 'error',
                                size: "20",
                                className: styles.errorTooltipTrigger()
                            }),
                            color: "error",
                            emphasis: true,
                            children: /*#__PURE__*/ jsx("div", {
                                children: errorTooltipText
                            })
                        }),
                        /*#__PURE__*/ jsx(Select.Icon, {
                            className: styles.iconRight(),
                            children: /*#__PURE__*/ jsx(CdnIcon, {
                                iconName: "arrow_drop_down",
                                size: "20"
                            })
                        })
                    ]
                }),
                isErrorNew && errorText ? /*#__PURE__*/ jsx("div", {
                    className: styles.errorText(),
                    children: errorText
                }) : null,
                !isErrorNew && helperText ? /*#__PURE__*/ jsx("div", {
                    className: styles.helperText(),
                    children: helperText
                }) : null,
                /*#__PURE__*/ jsx(Select.Portal, {
                    container: portalContainer || void 0,
                    children: /*#__PURE__*/ jsx(Select.Positioner, {
                        className: popupPositionerClassName,
                        side: "bottom",
                        sideOffset: 5,
                        alignItemWithTrigger: false,
                        collisionAvoidance: {
                            side: 'shift'
                        },
                        children: /*#__PURE__*/ jsx(Select.Popup, {
                            className: cn(styles.popupMenu(), menuWidth ? `w-[${menuWidth}]` : 'w-[var(--anchor-width)]'),
                            children: menuItems?.map((item)=>{
                                const itemStyles = selectVariants({
                                    itemSelected: item.value === value,
                                    itemDisabled: item?.disabled,
                                    itemSuffixInline: item?.suffixIsInline
                                });
                                return /*#__PURE__*/ jsxs(Select.Item, {
                                    value: item.value,
                                    label: item.label,
                                    className: itemStyles.popupItem(),
                                    disabled: item?.disabled,
                                    children: [
                                        /*#__PURE__*/ jsxs("div", {
                                            className: itemStyles.itemLabelIconSuffixWrapper(),
                                            children: [
                                                /*#__PURE__*/ jsxs("div", {
                                                    className: itemStyles.itemLabelIconWrapper(),
                                                    children: [
                                                        item.icon && /*#__PURE__*/ jsx("div", {
                                                            className: itemStyles.itemIcon(),
                                                            children: /*#__PURE__*/ jsx(CdnIcon, {
                                                                iconName: item.icon,
                                                                size: "24"
                                                            })
                                                        }),
                                                        /*#__PURE__*/ jsxs("div", {
                                                            className: itemStyles.itemLabelWrapper(),
                                                            children: [
                                                                /*#__PURE__*/ jsx("div", {
                                                                    className: itemStyles.itemLabel(),
                                                                    children: item.label
                                                                }),
                                                                item.helperText && /*#__PURE__*/ jsx("div", {
                                                                    className: itemStyles.itemLabelHelper(),
                                                                    children: item.helperText
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                }),
                                                item.suffixText && /*#__PURE__*/ jsx("div", {
                                                    className: itemStyles.itemSuffix(),
                                                    children: item.suffixText
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ jsx("div", {
                                            className: itemStyles.itemSelectedIconWrapper(),
                                            children: item.value === value && /*#__PURE__*/ jsx(CdnIcon, {
                                                iconName: "check",
                                                size: "20",
                                                className: itemStyles.itemSelectedIcon()
                                            })
                                        })
                                    ]
                                }, item.value);
                            })
                        })
                    })
                })
            ]
        })
    });
}
export { Select_Select as Select };
